import {StrictMode} from 'react';
import {createRoot} from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import App from './App.tsx';
import './index.css';

declare global {
  interface Window {
    dataLayer?: IArguments[];
    gtag?: (...args: unknown[]) => void;
  }
}

const COOKIE_STORAGE_KEY = 'financialmetrics_cookie_consent_v1';

type CookiePreferences = {
  essential: true;
  analytics: boolean;
  advertising: boolean;
  updatedAt: string;
  version: 1;
};

const scheduleThirdPartyLoad = (callback: () => void) => {
  if (typeof window === 'undefined') return;

  let hasRun = false;
  const run = () => {
    if (hasRun) return;
    hasRun = true;
    callback();
  };

  const interactionEvents = ['pointerdown', 'keydown', 'touchstart', 'scroll'];
  interactionEvents.forEach((eventName) => {
    window.addEventListener(eventName, run, { once: true, passive: true });
  });

  window.setTimeout(run, 12000);
};

const loadScriptOnce = (id: string, src: string, attributes: Record<string, string> = {}) => {
  if (document.getElementById(id)) return;

  const script = document.createElement('script');
  script.id = id;
  script.async = true;
  script.src = src;

  Object.entries(attributes).forEach(([key, value]) => {
    script.setAttribute(key, value);
  });

  document.head.appendChild(script);
};

const gaId = import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-35TG683G0B';

const loadCookiePreferences = () => {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(COOKIE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookiePreferences;
    return parsed?.version === 1 ? parsed : null;
  } catch {
    return null;
  }
};

const loadAnalytics = () => {
  if (!gaId || typeof window === 'undefined' || window.gtag) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer?.push(arguments);
  };

  window.gtag('js', new Date());
  window.gtag('config', gaId, { send_page_view: false });

  scheduleThirdPartyLoad(() => {
    loadScriptOnce('financialmetrics-google-analytics', `https://www.googletagmanager.com/gtag/js?id=${gaId}`);
  });
};

const applyCookiePreferences = (preferences: CookiePreferences | null) => {
  if (preferences?.analytics) {
    loadAnalytics();
  }
};

if (typeof window !== 'undefined') {
  applyCookiePreferences(loadCookiePreferences());
  window.addEventListener('financialmetrics:cookie-consent', (event) => {
    applyCookiePreferences((event as CustomEvent<CookiePreferences>).detail || null);
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </StrictMode>,
);
